// ============================================================================
// CHAT MESSAGE COMPONENT
// Individual message bubbles (user/assistant) with reference chips
// ============================================================================

import React from 'react';
import ReactMarkdown from 'react-markdown';
import { ChatMessage } from '../../types';

interface ChatMessageComponentProps {
  message: ChatMessage;
}

export function ChatMessageComponent({ message }: ChatMessageComponentProps) {
  const isUser = message.role === 'user';

  return (
    <>
    <style>{chatMarkdownStyles}</style>
    <div
      style={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        marginBottom: '8px'
      }}
    >
      <div
        style={{
          maxWidth: '85%',
          padding: '12px 16px',
          borderRadius: '12px',
          background: isUser
            ? 'linear-gradient(135deg, #00c6c2, #059669)'
            : '#f3f4f6',
          color: isUser ? 'white' : '#052831',
          fontSize: '0.9rem',
          lineHeight: '1.5',
          boxShadow: isUser
            ? '0 2px 8px rgba(0,198,194,0.2)'
            : '0 1px 3px rgba(0,0,0,0.1)'
        }}
      >
        {/* Message Content */}
        {isUser ? (
          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {message.content}
          </div>
        ) : (
          <div className="chat-markdown" style={{ wordBreak: 'break-word' }}>
            <ReactMarkdown>{message.content}</ReactMarkdown>
          </div>
        )}

        {/* References */}
        {message.references && message.references.length > 0 && (
          <div
            style={{
              marginTop: '12px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px'
            }}
          >
            {message.references.map((ref) => (
              <div
                key={ref.id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px',
                  background: isUser ? 'rgba(255,255,255,0.2)' : 'rgba(0,198,194,0.1)',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = isUser
                    ? 'rgba(255,255,255,0.3)'
                    : 'rgba(0,198,194,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isUser
                    ? 'rgba(255,255,255,0.2)'
                    : 'rgba(0,198,194,0.1)';
                }}
                title={ref.excerpt || ref.label}
              >
                {getRefIcon(ref.type)} {ref.label}
              </div>
            ))}
          </div>
        )}

        {/* Timestamp */}
        <div
          style={{
            marginTop: '8px',
            fontSize: '0.7rem',
            opacity: 0.7,
            textAlign: isUser ? 'right' : 'left'
          }}
        >
          {new Date(message.timestamp).toLocaleTimeString()}
        </div>
      </div>
    </div>
    </>
  );
}

const chatMarkdownStyles = `
.chat-markdown p { margin: 0 0 8px 0; }
.chat-markdown p:last-child { margin-bottom: 0; }
.chat-markdown ul, .chat-markdown ol { margin: 4px 0 8px 0; padding-left: 20px; }
.chat-markdown li { margin-bottom: 2px; }
.chat-markdown h1, .chat-markdown h2, .chat-markdown h3, .chat-markdown h4 {
  margin: 8px 0 4px 0; font-weight: 600;
}
.chat-markdown h1 { font-size: 1.1em; }
.chat-markdown h2 { font-size: 1.05em; }
.chat-markdown h3 { font-size: 1em; }
.chat-markdown code {
  background: rgba(0,0,0,0.06); padding: 1px 4px; border-radius: 3px; font-size: 0.85em;
}
.chat-markdown pre {
  background: rgba(0,0,0,0.06); padding: 8px; border-radius: 6px; overflow-x: auto; margin: 4px 0 8px 0;
}
.chat-markdown pre code { background: none; padding: 0; }
.chat-markdown blockquote {
  border-left: 3px solid rgba(0,0,0,0.15); margin: 4px 0 8px 0; padding-left: 10px; opacity: 0.85;
}
.chat-markdown strong { font-weight: 600; }
.chat-markdown a { text-decoration: underline; }
.chat-markdown hr { border: none; border-top: 1px solid rgba(0,0,0,0.1); margin: 8px 0; }
.chat-markdown table { border-collapse: collapse; margin: 4px 0 8px 0; font-size: 0.85em; }
.chat-markdown th, .chat-markdown td { border: 1px solid rgba(0,0,0,0.1); padding: 4px 8px; }
.chat-markdown th { font-weight: 600; background: rgba(0,0,0,0.04); }
`;

function getRefIcon(type: string): string {
  switch (type) {
    case 'avatar':
      return '🤖';
    case 'transcript':
      return '🎤';
    case 'guidance':
      return '💡';
    case 'requirement':
      return '📋';
    default:
      return '🔗';
  }
}
